import { useTranslation } from 'react-i18next';
import type { HomeQuestion } from '@/types/survey.types';
import type { QuizAnswer } from '../hooks/useInlineQuiz';
import { LIKERT_FACES } from './MatrixBody';

interface QuestionBodyProps {
  q: HomeQuestion;
  eIdx: number;
  selected: QuizAnswer;
  setAnswer: (eIdx: number, value: QuizAnswer) => void;
}

export function QuestionBody({ q, eIdx, selected, setAnswer }: QuestionBodyProps) {
  const { t } = useTranslation();
  const BEHAVIOR_COLORS = ['#342F82', '#10B981', '#F59E0B', '#EF4444'];

  if (q.type === 'nps') {
    return (
      <div className="home-nps-inline">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
          <button key={n} type="button"
            className={`home-nps-btn${selected === n ? ' selected' : ''}`}
            onClick={() => setAnswer(eIdx, n)} aria-label={String(n)}>{n}</button>
        ))}
      </div>
    );
  }

  if (q.type === 'open') {
    return (
      <textarea className="home-q-open" rows={3}
        placeholder={t('survey.openPlaceholder', { defaultValue: 'Escribe tu respuesta aquí…' })}
        value={typeof selected === 'string' ? selected : ''}
        onChange={e => setAnswer(eIdx, e.target.value)} />
    );
  }

  if (q.type === 'multiple' || q.type === 'behaviors') {
    const opts = q.options ?? [];
    const isBehaviors = q.type === 'behaviors';
    return (
      <div className="home-q-mc">
        {opts.map((opt, oi) => {
          const c = isBehaviors ? (BEHAVIOR_COLORS[oi] ?? '#9ca3af') : undefined;
          return (
            <button key={oi} type="button"
              className={`home-q-mc-opt${selected === oi ? ' selected' : ''}`}
              style={isBehaviors && selected === oi && c ? { borderColor: c, background: `${c}12` } : {}}
              onClick={() => setAnswer(eIdx, oi)}>
              <span className={`home-q-mc-dot${selected === oi ? ' selected' : ''}`}
                style={isBehaviors && selected === oi && c ? { background: c, borderColor: c } : {}} />
              <span className="home-q-mc-label">{opt}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // likert — emoji faces + No observable
  const NO_OBS_COLOR = '#9ca3af';
  return (
    <div className="home-q-faces">
      {LIKERT_FACES.map(face => (
        <button key={face.value} type="button"
          className={`home-q-face-btn${selected === face.value ? ' selected' : ''}`}
          style={selected === face.value ? { borderColor: face.color, background: `${face.color}12` } : {}}
          onClick={() => setAnswer(eIdx, face.value)} aria-label={t(face.labelKey)}>
          <svg width="30" height="30" viewBox="0 0 36 36" fill="none">
            <circle cx="18" cy="18" r="16" fill={face.color} opacity=".15" stroke={face.color} strokeWidth="1.5" />
            <circle cx="12" cy={face.eyeY} r="2" fill={face.color} />
            <circle cx="24" cy={face.eyeY} r="2" fill={face.color} />
            <path d={face.path} stroke={face.color} strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <span className="home-q-face-label" style={{ color: face.color }}>{t(face.labelKey)}</span>
        </button>
      ))}
      <button type="button"
        className={`home-q-face-btn home-q-face-btn--no-obs${selected === 0 ? ' selected' : ''}`}
        style={selected === 0 ? { borderColor: NO_OBS_COLOR, background: `${NO_OBS_COLOR}20` } : {}}
        onClick={() => setAnswer(eIdx, 0)} aria-label={t('survey.likert.notObservable')}>
        <i className="fas fa-eye-slash" style={{ color: NO_OBS_COLOR, fontSize: '1.3rem' }} />
        <span className="home-q-face-label" style={{ color: NO_OBS_COLOR }}>{t('survey.likert.notObservable')}</span>
      </button>
    </div>
  );
}
